import { useEffect, useState } from 'react';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { T, FONT, fmtMoney, mix } from '../theme';
import { Card, Eyebrow, H1, Stage } from '../primitives';
import { DeltaPanel } from './DeltaPanel';
import { DivergencePanel } from './DivergencePanel';
import { BeforeAfterPanel } from './BeforeAfterPanel';
import { GenericPanel } from './GenericPanel';
import { planStartYearOf, type OptimizationVM } from '../../formatters/deckContent';
import type { ReportEnrichment, WebReportPayload } from '../../types/webReport';

interface Props {
  vm: OptimizationVM;
  index: number;
  count: number;
  onBack: () => void;
  onSwitch: (i: number) => void;
  payload: WebReportPayload;
  enrichment: ReportEnrichment | null;
  interactive: boolean;
  /** Print: start optimized and hide the toggle. */
  forceOptimized?: boolean;
  /** Print: fixed chart width, animations off. */
  chartWidth?: number;
}

/** The reusable optimization deep-dive template, rendered once per optimization. */
export function OptimizationDeepDive({
  vm,
  index,
  count,
  onBack,
  onSwitch,
  payload,
  enrichment,
  interactive,
  forceOptimized,
  chartWidth,
}: Props) {
  const [optimized, setOptimized] = useState(!!forceOptimized);

  useEffect(() => {
    setOptimized(!!forceOptimized);
  }, [vm.key, forceOptimized]);

  const hasToggle = !forceOptimized && vm.framing !== 'delta' && vm.framing !== 'generic';
  // The case's real plan-year-1 calendar year (not the viewing date — a report
  // opened next January must not shift its schedule).
  const planStartYear = planStartYearOf(enrichment);

  return (
    <Stage>
      {/* breadcrumb + opt switcher (interactive only) */}
      {interactive && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.gray,
              fontSize: 13,
              fontFamily: FONT,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <ChevronLeft size={16} /> All optimizations
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                onClick={() => onSwitch(i)}
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 5,
                  border: 'none',
                  cursor: 'pointer',
                  background: i === index ? T.teal : T.grayLine,
                  transition: 'background .2s',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* value tab — identical spot for every optimization, so impact is comparable */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 9,
          background: T.growthSoft,
          border: `1px solid ${T.growth}`,
          borderRadius: 10,
          padding: '9px 16px',
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 22, fontWeight: 700, color: T.growth, lineHeight: 1 }}>{vm.valueAddedLabel}</span>
        <span style={{ fontSize: 12.5, color: T.growth }}>is added to the plan</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, marginBottom: 8 }}>
        <H1>{vm.title}</H1>
        {hasToggle && (
          <button
            onClick={() => setOptimized((v) => !v)}
            style={{
              flexShrink: 0,
              marginTop: 4,
              padding: '13px 22px',
              borderRadius: 10,
              cursor: 'pointer',
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: 700,
              border: 'none',
              background: optimized ? T.navy : T.growth,
              color: '#fff',
              boxShadow: optimized ? 'none' : `0 4px 14px ${mix(T.growth, 40)}`,
              transition: 'all .2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              animation: optimized ? 'none' : 'pulseGreen 2s ease-in-out infinite',
            }}
          >
            {optimized ? '← Hide optimized' : 'Show optimized'}
            {!optimized && <ArrowRight size={17} />}
          </button>
        )}
      </div>
      {hasToggle && !optimized && (
        <div style={{ fontSize: 13, color: T.growth, fontWeight: 600, marginBottom: 6 }}>
          Tap “Show optimized” to see the improvement appear →
        </div>
      )}

      {/* Print (fixed chart width) stacks the panels so 720px charts fit. */}
      <div style={{ display: 'grid', gridTemplateColumns: chartWidth != null ? '1fr' : '1.5fr 1fr', gap: 24, marginTop: 14 }}>
        {/* chart side — framing-specific */}
        <Card>
          {vm.framing === 'delta' && <DeltaPanel vm={vm} />}
          {vm.framing === 'divergence' && (
            <DivergencePanel vm={vm} payload={payload} optimized={optimized} width={chartWidth} />
          )}
          {vm.framing === 'beforeafter' && (
            <BeforeAfterPanel vm={vm} payload={payload} enrichment={enrichment} optimized={optimized} width={chartWidth} />
          )}
          {vm.framing === 'generic' && <GenericPanel vm={vm} />}
        </Card>

        {/* strategy panel — persists across toggle */}
        <div>
          <Card style={{ marginBottom: 14 }}>
            <Eyebrow>{vm.strategyLabel}</Eyebrow>
            <div style={{ fontWeight: 700, color: T.navy, fontSize: 17, marginBottom: 8, lineHeight: 1.25 }}>
              {vm.strategyTitle}
            </div>
            <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.6 }}>{vm.strategyBody}</div>
          </Card>
          <Card>
            <Eyebrow>{vm.whyLabel}</Eyebrow>
            <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.6 }}>{vm.whyBody}</div>
          </Card>
          {vm.roth && vm.roth.schedule.length > 0 && (
            <Card style={{ marginTop: 14 }}>
              <Eyebrow>Conversion schedule</Eyebrow>
              {vm.roth.schedule.map((entry) => (
                <div
                  key={entry.year}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13 }}
                >
                  <span style={{ color: T.gray }}>{planStartYear + entry.year - 1}</span>
                  <span style={{ fontWeight: 700, color: T.navy }}>{fmtMoney(entry.amount)}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </Stage>
  );
}
